import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export const DASHBOARD_SECTIONS = [
  { id: "reminders", label: "Smart Reminders" },
  { id: "today", label: "Today (shoots & deliverables due)" },
  { id: "business", label: "Business stats" },
  { id: "ontime", label: "On-Time Delivery" },
  { id: "time", label: "Time Tracking" },
  { id: "recent", label: "Recent Orders" },
];

export const DEFAULT_LAYOUT = { order: DASHBOARD_SECTIONS.map((s) => s.id), hidden: [] };

export function normalizeLayout(layout) {
  const ids = DASHBOARD_SECTIONS.map((s) => s.id);
  const order = (layout?.order || []).filter((id) => ids.includes(id));
  ids.forEach((id) => { if (!order.includes(id)) order.push(id); });
  return { order, hidden: (layout?.hidden || []).filter((id) => ids.includes(id)) };
}

const Row = ({ id, hidden, onToggle }) => {
  const controls = useDragControls();
  const label = DASHBOARD_SECTIONS.find((s) => s.id === id)?.label || id;
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls}
      className={`flex select-none items-center gap-3 rounded-lg border bg-card px-3 py-3 ${hidden ? "border-dashed border-border opacity-60" : "border-border"}`}
      data-testid={`customize-row-${id}`}>
      <button onPointerDown={(e) => controls.start(e)} aria-label={`Drag ${label}`}
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
        data-testid={`customize-drag-${id}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      <span className={`flex-1 text-sm font-medium ${hidden ? "line-through" : ""}`}>{label}</span>
      <button onClick={() => onToggle(id)} data-testid={`customize-toggle-${id}`}
        className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
        {hidden ? <><EyeOff className="h-3.5 w-3.5" /> Hidden</> : <><Eye className="h-3.5 w-3.5" /> Shown</>}
      </button>
    </Reorder.Item>
  );
};

export const DashboardCustomize = ({ open, onOpenChange, layout, onChange }) => {
  const toggle = (id) => onChange({
    ...layout,
    hidden: layout.hidden.includes(id) ? layout.hidden.filter((h) => h !== id) : [...layout.hidden, id],
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="customize-panel">
        <SheetHeader>
          <SheetTitle>Customize dashboard</SheetTitle>
          <SheetDescription>Drag sections into the order you want, or hide the ones you don't need. Changes save automatically.</SheetDescription>
        </SheetHeader>
        <Reorder.Group axis="y" values={layout.order} onReorder={(order) => onChange({ ...layout, order })}
          className="mt-6 space-y-2" data-testid="customize-list">
          {layout.order.map((id) => (
            <Row key={id} id={id} hidden={layout.hidden.includes(id)} onToggle={toggle} />
          ))}
        </Reorder.Group>
        <Button variant="outline" className="mt-6 gap-2" onClick={() => onChange(DEFAULT_LAYOUT)} data-testid="customize-reset">
          <RotateCcw className="h-4 w-4" /> Reset to default
        </Button>
      </SheetContent>
    </Sheet>
  );
};
